// IndexedDB-backed key/value store — the async storage engine that replaces
// localStorage across the app.
//
// Historically every collection lived in localStorage (see services/db.ts and
// the per-device settings services), which capped the whole dataset at the
// ~5 MB localStorage quota — a real ceiling once the sales array grows. This
// module moves all of that into IndexedDB, which offers far more headroom.
//
// To keep the migration low-risk, IndexedDB is used as a *string* key/value
// store that mirrors localStorage one-for-one: callers still hold the exact
// same JSON strings they did before (their JSON.parse / JSON.stringify and any
// value comparisons are unchanged), so only the read/write plumbing became
// async. The one-time migration below therefore copies every existing
// localStorage entry across verbatim.

import { openDB, IDBPDatabase } from 'idb';

const DB_NAME = 'epos';
const STORE = 'kv';
const DB_VERSION = 1;

// Internal bookkeeping key marking that the one-time localStorage import has
// run, so it happens exactly once per device.
const MIGRATED_FLAG = '__migrated_from_localstorage';

let dbPromise: Promise<IDBPDatabase> | null = null;

function getDB(): Promise<IDBPDatabase> {
  if (!dbPromise) {
    dbPromise = openDB(DB_NAME, DB_VERSION, {
      upgrade(db) {
        if (!db.objectStoreNames.contains(STORE)) {
          db.createObjectStore(STORE);
        }
      },
    });
  }
  return dbPromise;
}

// Read a raw string value (or null when the key is absent) — the async
// equivalent of localStorage.getItem.
export async function idbGetRaw(key: string): Promise<string | null> {
  const v = await (await getDB()).get(STORE, key);
  return v == null ? null : (v as string);
}

// Write a raw string value — the async equivalent of localStorage.setItem.
export async function idbSetRaw(key: string, value: string): Promise<void> {
  await (await getDB()).put(STORE, value, key);
}

// Delete a key — the async equivalent of localStorage.removeItem.
export async function idbDel(key: string): Promise<void> {
  await (await getDB()).delete(STORE, key);
}

// Every stored key (excludes the internal migration flag), so backup/export
// code can enumerate the dataset the way it used to walk localStorage.
export async function idbAllKeys(): Promise<string[]> {
  const keys = (await (await getDB()).getAllKeys(STORE)) as IDBValidKey[];
  return keys.map((k) => String(k)).filter((k) => k !== MIGRATED_FLAG);
}

// Every key/value pair as a plain object, for the backup snapshot builders.
export async function idbEntries(): Promise<Record<string, string>> {
  const db = await getDB();
  const tx = db.transaction(STORE, 'readonly');
  const store = tx.store;
  const keys = (await store.getAllKeys()) as IDBValidKey[];
  const out: Record<string, string> = {};
  for (const k of keys) {
    const key = String(k);
    if (key === MIGRATED_FLAG) continue;
    out[key] = (await store.get(k)) as string;
  }
  await tx.done;
  return out;
}

// ---- Synchronously-readable cached keys ----------------------------------
// Some per-device settings (chosen printers, feature toggles, licence device
// id) are read synchronously by components during render and by the pure print
// builders, which can't await IndexedDB. `cachedKey` wraps a single IDB key as
// an in-memory value that reads synchronously and persists asynchronously: the
// cache is filled once at boot (primeCaches, run from initDb) and refreshed on
// every write, so it always matches what this device last stored.

type Prime = () => Promise<void>;
const primers: Prime[] = [];

export interface CachedKey<T> {
  get(): T;
  set(value: T): Promise<void>;
  clear(): Promise<void>;
}

export function cachedKey<T>(key: string, fallback: T): CachedKey<T> {
  let cache = fallback;
  primers.push(async () => {
    const raw = await idbGetRaw(key);
    if (raw != null) {
      try {
        cache = JSON.parse(raw) as T;
      } catch {
        /* keep fallback on malformed value */
      }
    }
  });
  return {
    get: () => cache,
    set: async (value: T) => {
      cache = value;
      await idbSetRaw(key, JSON.stringify(value));
    },
    clear: async () => {
      cache = fallback;
      await idbDel(key);
    },
  };
}

export interface CachedRawKey {
  get(): string;
  set(value: string): Promise<void>;
  clear(): Promise<void>;
}

// Like cachedKey but for values stored as plain strings rather than JSON (device
// ids, ISO timestamps, flags) — matching how they lived in localStorage, so the
// boot migration reads them back unchanged.
export function cachedRawKey(key: string, fallback: string): CachedRawKey {
  let cache = fallback;
  primers.push(async () => {
    const raw = await idbGetRaw(key);
    if (raw != null) cache = raw;
  });
  return {
    get: () => cache,
    set: async (value: string) => {
      cache = value;
      await idbSetRaw(key, value);
    },
    clear: async () => {
      cache = fallback;
      await idbDel(key);
    },
  };
}

// Fill every cachedKey from storage. Awaited during initDb so the synchronous
// getters return real values from first render.
export async function primeCaches(): Promise<void> {
  await Promise.all(primers.map((p) => p()));
}

// Open the database and, on first run only, import every existing localStorage
// entry so upgrading installs keep all their data. Must be awaited before the
// app reads any storage (see src/index.tsx). Safe to call more than once.
export async function initDb(): Promise<void> {
  const db = await getDB();
  const alreadyMigrated = await db.get(STORE, MIGRATED_FLAG);
  if (!alreadyMigrated) {
    try {
      const tx = db.transaction(STORE, 'readwrite');
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key == null) continue;
        const raw = localStorage.getItem(key);
        if (raw != null) await tx.store.put(raw, key);
      }
      await tx.store.put(true, MIGRATED_FLAG);
      await tx.done;
    } catch (err) {
      // A failed import shouldn't wedge startup; mark it done so the app runs on
      // an empty (seeded) dataset rather than retrying a broken copy every launch.
      console.warn('localStorage → IndexedDB migration failed:', err);
      await db.put(STORE, true, MIGRATED_FLAG);
    }
  }
  // Fill the synchronous caches from the (now migrated) data before first render.
  await primeCaches();
}
